'use client'
import { Teams } from '@glovo/organisation'
import Image from 'next/image'
import type { FC } from 'react'
import { useMemo } from 'react'
import React, { useState } from 'react'
import { GenericMap } from '../../components/generic-map/GenericMap'
import styles from './SetEntrance.module.scss'
import { isLocationInDeliveryArea } from '@/location/use-cases'
import { useLocalisation } from '@/tools/i18n/client'
import { getImageUrl } from '@/tools/image-service/client'
import { withErrorBoundary } from '@/tools/o11y/client'
import {
  Button,
  ButtonSize,
  ChevronBackwardIcon,
  ExclamationTriangleIcon,
  IconBlob,
  IconBlobSize,
  IconBlobType,
  IconButton,
  IconButtonSize,
  IconButtonVariants,
} from '@/tools/ui'
import { useAddressBookContext } from '@/user/address-book/AddressBookContext'
import type { AddressCoordinates } from '@/user/address-book/entities'
import type { GoogleCoordinates } from '@/user/address-book/types'
import { MapTooltip } from '@/user/address-book/ui/components/map-tooltip/MapTooltip'
import { SET_ENTRANCE_ZOOM_LEVEL } from '@/user/address-book/ui/constants'

type TooltipToShow = 'initial' | 'inZone' | 'outOfZone' | 'farFromAddress' | null

const SetEntranceComponent: FC = () => {
  const { t } = useLocalisation()
  const {
    navigate,
    setHasRefinedEntrance,
    setAddress,
    addressData,
    isMobile,
    originalCoordinates,
    deviceCurrentLocation: { deviceLocation },
  } = useAddressBookContext()

  const APPROX_METRE_IN_DEGREES_LATITUDE = 1 / 111000
  const TWO_HUNDRED_METRES_IN_DEGREES = APPROX_METRE_IN_DEGREES_LATITUDE * 200

  const [selectedLocation, setSelectedLocation] = useState<AddressCoordinates | null>(null)
  const [isEntranceInArea, setIsEntranceInArea] = useState<boolean>(true)
  const [tooltipToShow, setTooltipToShow] = useState<TooltipToShow>('initial')

  const initialTooltipImgSrc = getImageUrl({
    source: 'dh:customer-assets-glovo/address_flow/address_book/courier_confused.svg',
  })
  const inZoneTooltipImgSrc = getImageUrl({
    source: 'dh:customer-assets-glovo/address_flow/address_book/courier_love.svg',
  })
  const outOfZoneTooltipImgSrc = getImageUrl({
    source: 'dh:customer-assets-glovo/address_flow/address_book/bike_icon.svg',
  })
  const googleLogoSrc = getImageUrl({
    source: 'dh:customer-assets-glovo/address_flow/address_book/powered_by_google.svg',
  })

  const initialTooltipProps = {
    title: t('address-list.point-to-the-entrance'),
    description: t('address-list.this-helps-the-courier'),
    icon: <Image alt="Confused courier icon" height={79} src={initialTooltipImgSrc} width={56} />,
    width: 310,
  }

  const inZoneTooltipProps = {
    title: t('address-list.this-will-help'),
    description: t('address-list.make-sure-pin-on-street'),
    icon: <Image alt="Courier love icon" height={81} src={inZoneTooltipImgSrc} width={56} />,
    width: 310,
  }

  const outOfZoneTooltipProps = {
    title: t('address-list.out-of-delivery-area'),
    description: t('address-list.we-cant-deliver-here'),
    icon: (
      <Image
        alt="Bike icon"
        className={styles.bikeIcon}
        height={18}
        src={outOfZoneTooltipImgSrc}
        width={20}
      />
    ),
    width: 310,
  }

  const farFromAddressTooltipProps = {
    title: t('address-set-entrance.tooltip-title'),
    description: t('address-set-entrance.tooltip-subtitle'),
    icon: (
      <IconBlob
        icon={ExclamationTriangleIcon}
        size={IconBlobSize.medium}
        type={IconBlobType.promotion}
      />
    ),
    width: 310,
  }

  const positionOverride = useMemo(
    () =>
      addressData.latitude || addressData.longitude
        ? { lat: addressData.latitude, lng: addressData.longitude }
        : undefined,
    [addressData.latitude, addressData.longitude]
  )

  const userMarkPosition = useMemo(() => {
    const coordinates = deviceLocation?.coordinates
    return coordinates ? { lat: coordinates.latitude, lng: coordinates.longitude } : undefined
  }, [deviceLocation])

  const getTooltipProps = () => {
    if (tooltipToShow === 'inZone') {
      return inZoneTooltipProps
    }
    if (tooltipToShow === 'outOfZone') {
      return outOfZoneTooltipProps
    }
    if (tooltipToShow === 'farFromAddress') {
      return farFromAddressTooltipProps
    }
    return initialTooltipProps
  }

  const tooltip = tooltipToShow ? <MapTooltip {...getTooltipProps()} /> : null

  const isEntranceFarFromOriginalCoords = (newCoordinates: AddressCoordinates) => {
    const latitudeIsFar =
      Math.abs(newCoordinates.latitude - originalCoordinates.latitude) >
      TWO_HUNDRED_METRES_IN_DEGREES
    const longitudeIsFar =
      Math.abs(newCoordinates.longitude - originalCoordinates.longitude) >
      TWO_HUNDRED_METRES_IN_DEGREES
    return latitudeIsFar || longitudeIsFar
  }

  const fetchAddressDetails = async (location: AddressCoordinates) => {
    const isEntranceInArea = await isLocationInDeliveryArea(location)
    setIsEntranceInArea(isEntranceInArea)
    const isEntranceTooFar = isEntranceFarFromOriginalCoords(location)
    if (!isEntranceInArea) {
      setTooltipToShow('outOfZone')
    } else if (isEntranceTooFar) {
      setTooltipToShow('farFromAddress')
    } else {
      setTooltipToShow('inZone')
    }
  }

  const handleLocationChange = async (location: GoogleCoordinates) => {
    setTooltipToShow(null)
    const coordinates = { latitude: location?.lat, longitude: location?.lng }
    await fetchAddressDetails(coordinates)
    setSelectedLocation(coordinates)
  }

  const handleDragStart = () => {
    setTooltipToShow(null)
  }

  const handleSubmit = () => {
    if (selectedLocation && isEntranceInArea) {
      setAddress({
        ...addressData,
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
      })
      setHasRefinedEntrance(true)
    }
    navigate('onBack')
  }

  const handleNavigateBack = () => {
    navigate('onBack')
  }

  return (
    <div className={isMobile ? styles.setEntranceMobile : styles.setEntranceDesktop}>
      {!isMobile && (
        <div className={styles.titleWrapperDesktop}>
          <IconButton
            aria-label={t('glovo.common.back')}
            data-testid="back-button"
            icon={ChevronBackwardIcon}
            size={IconButtonSize.medium}
            variant={IconButtonVariants.transparent}
            onClick={handleNavigateBack}
          />
          <h3 className={styles.titleDesktop}>{t('address-set-entrance.title')}</h3>
        </div>
      )}

      <div className={styles.mapContainer}>
        <GenericMap
          isOutOfZone={!isEntranceInArea}
          mapId="set-entrance-map"
          positionOverride={positionOverride}
          tooltip={tooltip}
          userMarkPosition={userMarkPosition}
          zoomOverride={SET_ENTRANCE_ZOOM_LEVEL}
          onDragStart={handleDragStart}
          onLocationChange={handleLocationChange}
        />
      </div>
      <div className={styles.footerContainer}>
        {!isMobile && (
          <div className={styles.instructionWrapper}>
            <p className={styles.instruction}>{t('address-set-entrance.instruction')}</p>
            <Image alt="Google logo" height={18} src={googleLogoSrc} width={121} />
          </div>
        )}
        <Button
          block={true}
          disabled={!isEntranceInArea}
          size={ButtonSize.medium}
          onClick={handleSubmit}
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  )
}

export const SetEntrance = withErrorBoundary(SetEntranceComponent, {
  id: 'user.address_book.step.set_entrance',
  owner: Teams.WEBX,
})
